'use client'
import type { Data } from '@repo/strapi'
import { useTranslations } from 'next-intl'
import { useRef } from 'react'
import { BillingDetails } from '@/components/page-builder/global/pricingV1/overview/BillingDetails'
import { PlanHighlights } from '@/components/page-builder/global/pricingV1/overview/PlanHighlights'
import { PlanCallToAction } from '@/components/page-builder/global/pricingV1/shared/PlanCallToAction'
import { getColsClassName } from '@/components/page-builder/global/pricingV1/utils/table-helper'
import Heading from '@/components/typography/Heading'
import { Paragraph } from '@/components/typography/Paragraph'
import {
	PRICING_CTA_SECTIONS,
	SITE_HEADER_OFFSET_DESKTOP,
	STICKY_HEADER_BOTTOM_THRESHOLD,
	STICKY_HEADER_FADE_DISTANCE,
	STICKY_HEADER_FADE_THRESHOLD,
} from '@/constants/pricing.constants'
import { useRefSticky } from '@/hooks/useRefSticky'
import { useScrollFadeOut } from '@/hooks/useScrollFadeOut'
import { cn } from '@/lib/styles'
import { PRICING_TEST_IDS } from '@/lib/test-ids'

export function OverviewTable({
	data,
	currentTab,
	yearly = false,
	className,
	stickyTopOffset = SITE_HEADER_OFFSET_DESKTOP,
}: {
	data?: Data.Component<'shared.pricing-plan-detail'>[] | null
	currentTab?: string | null
	yearly?: boolean
	className?: string
	stickyTopOffset?: number
}) {
	const t = useTranslations('Pricing')

	const tableHeaderRef = useRef<HTMLDivElement | null>(null)
	const containerRef = useRef<HTMLDivElement | null>(null)

	const { isSticky, isWithinBottomThreshold } = useRefSticky(
		tableHeaderRef,
		stickyTopOffset,
		STICKY_HEADER_BOTTOM_THRESHOLD,
	)
	const opacity = useScrollFadeOut(
		containerRef,
		STICKY_HEADER_FADE_THRESHOLD,
		STICKY_HEADER_FADE_DISTANCE,
	)

	const gridColsClassName = !currentTab ? getColsClassName(data?.length) : 'grid-cols-1'
	const rowClassName = cn('grid divide-x divide-neutral-700', gridColsClassName)

	const plansData = currentTab ? data?.filter((item) => item.title === currentTab) : data
	return (
		<div
			ref={containerRef}
			className={cn('relative pt-5 transition-opacity duration-200', !yearly && 'pt-15', className)}
			style={{ opacity }}
			data-test-id={PRICING_TEST_IDS.LIST_CONTENT}
		>
			{/* Table header */}
			<div
				ref={tableHeaderRef}
				className={cn(
					'grid lg:sticky lg:top-(--site-header-offset-desktop) lg:z-2 lg:pt-5 bg-brand-black',
				)}
			>
				{/* Most popular tag */}
				{yearly ? (
					<div className={cn('hidden lg:grid bg-brand-black', gridColsClassName)}>
						{plansData?.map((item) => (
							<div
								key={`${item?.id}-popular-header`}
								className={cn(
									'p-2 grid place-items-center',
									item.mostPopular &&
										'bg-neutral-light-100 text-brand-black rounded-t-lg lg:-ml-px lg:pl-px',
								)}
							>
								{item.mostPopular ? (
									<Paragraph fontSize="bodyBoldMedium" className="uppercase">
										{t('mostPopular')}
									</Paragraph>
								) : null}
							</div>
						))}
					</div>
				) : null}

				{/* Plan title row */}
				<div
					className={cn(
						rowClassName,
						isSticky &&
							'lg:border-b lg:border-b-neutral-700 lg:transition-[colors,opacity] lg:duration-300',
					)}
				>
					{plansData?.map(({ id, mostPopular, title }) => (
						<div
							key={`${id}-titles`}
							className={cn(
								'px-3.75 pt-7.5 lg:pt-5 pb-2.5 border-neutral-700 relative',
								'border-t first:rounded-tl-xl last:rounded-tr-xl first:border-l last:border-r',
								mostPopular && 'pl-4 pr-3.75 bg-surface-featured lg:-ml-px lg:border-l',
							)}
						>
							<Heading tag="h2" className="w-fit text-neutral-light-100" fontSize="headingMedium">
								{title}
							</Heading>

							{/* Gradient mask */}
							<div
								className={cn(
									'absolute -bottom-6.25 left-0 right-0 h-6 hidden lg:block',
									'bg-linear-to-b from-brand-black to-transparent',
									mostPopular ? 'from-surface-featured' : 'from-brand-black',
									isSticky && !isWithinBottomThreshold ? 'opacity-100' : 'opacity-0',
									'transition-opacity duration-200',
								)}
							/>
						</div>
					))}
				</div>
			</div>

			{/* Plan description row */}
			<div className={rowClassName}>
				{plansData?.map(({ id, mostPopular, description }) => (
					<div
						key={`${id}-description`}
						className={cn(
							'pb-5 px-3.75',
							'border-neutral-700 first:border-l last:border-r',
							mostPopular && 'bg-surface-featured',
						)}
					>
						<Paragraph fontSize="bodyRegularMedium" className="text-neutral-light-100">
							{description}
						</Paragraph>
					</div>
				))}
			</div>

			{/* Billing details row */}
			<div className={rowClassName}>
				{plansData?.map((item) => (
					<BillingDetails
						key={`${item?.id}-billing-details`}
						pricingItem={item}
						yearly={yearly}
						className={cn(
							'border-neutral-700 first:border-l last:border-r',
							item.mostPopular && 'bg-surface-featured',
						)}
					/>
				))}
			</div>

			{/* CTA's row */}
			<div className={rowClassName}>
				{plansData?.map(({ id, button, mostPopular, title }) => (
					<PlanCallToAction
						key={`${id}-cta`}
						button={button}
						planTitle={title ?? undefined}
						section={PRICING_CTA_SECTIONS.OVERVIEW}
						className={cn(
							'border-neutral-700 first:border-l last:border-r',
							mostPopular && 'bg-surface-featured',
						)}
						linkClassName="lg:whitespace-nowrap xl:flex xl:gap-2.5"
					/>
				))}
			</div>

			{/* Plan Highlights */}
			<div className={rowClassName}>
				{plansData?.map((item) => (
					<PlanHighlights
						key={`${item?.id}-highlights`}
						pricingItem={item}
						yearly={yearly}
						className={cn(
							'border-b border-neutral-700 first:rounded-bl-xl last:rounded-br-xl first:border-l last:border-r',
							item.mostPopular && 'bg-surface-featured',
						)}
					/>
				))}
			</div>
		</div>
	)
}
